import { loadPLY } from "./PLYLoader";
import { loadSplat } from "./SplatLoader";
import { loadKSplat } from "./KSplatLoader";
import { loadSPZ } from "./SPZLoader";

import { DEFAULT_GAUSSIAN_BUDGET } from "./helpers/GaussianSampling";
import { validateGaussianData } from "./helpers/GaussianData";

export { DEFAULT_GAUSSIAN_BUDGET };

/**
 * Loads a Gaussian scene, dispatches it to the appropriate format
 * decoder, and validates the normalized result.
 */
export async function loadGaussianScene(source, filename = "", options = {}) {
  const maxGaussians = options.maxGaussians ?? DEFAULT_GAUSSIAN_BUDGET;

  const response = await fetch(source);

  if (!response.ok) {
    throw new Error(
      `Failed to load Gaussian scene: ${response.status} ${response.statusText}`,
    );
  }

  const buffer = await response.arrayBuffer();
  const format = detectGaussianFormat(buffer, filename);

  let result;

  switch (format) {
    case "ply":
      result = loadPLY(buffer, { maxGaussians });
      break;

    case "splat":
      result = loadSplat(buffer, { maxGaussians });
      break;

    case "ksplat":
      result = loadKSplat(buffer, { maxGaussians });
      break;

    case "spz":
      result = await loadSPZ(buffer, { maxGaussians });
      break;

    default:
      throw new Error(`Unsupported Gaussian format: ${format}`);
  }

  validateGaussianData(result);

  return {
    ...result,
    format: format.toUpperCase(),
  };
}

/**
 * Detects a supported Gaussian format from the filename first,
 * then falls back to signatures and structural heuristics.
 */
export function detectGaussianFormat(buffer, filename = "") {
  const bytes = new Uint8Array(buffer);
  const lower = filename.toLowerCase();

  if (lower.endsWith(".ply")) return "ply";
  if (lower.endsWith(".splat")) return "splat";
  if (lower.endsWith(".ksplat")) return "ksplat";
  if (lower.endsWith(".spz")) return "spz";

  if (
    bytes.length >= 3 &&
    bytes[0] === 0x70 &&
    bytes[1] === 0x6c &&
    bytes[2] === 0x79
  ) {
    return "ply";
  }

  if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
    return "spz";
  }

  if (
    bytes.length >= 4 &&
    bytes[0] === 0x4e &&
    bytes[1] === 0x47 &&
    bytes[2] === 0x53 &&
    bytes[3] === 0x50
  ) {
    return "spz";
  }

  if (bytes.length >= 2 && bytes[0] === 0 && bytes[1] >= 1) {
    return "ksplat";
  }

  // Raw .splat records are fixed at 32 bytes each.
  if (bytes.length > 0 && bytes.length % 32 === 0) {
    return "splat";
  }

  throw new Error("Could not determine Gaussian scene format.");
}
